import React, { useState, useEffect, useContext, createContext } from "react";
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import moment from 'moment';

import { randomId } from '@mui/x-data-grid-generator';

import { ContextBlock } from 'context/ContextBlock';
import { ContextTraining } from 'context/ContextTraining';

import axios from 'api/axios';

const ContextTrainingSearch = createContext();

const TrainingSearchProvider = ({ children }) => {
  const navigate = useNavigate();

  //URL API
  const TRAINING_URL = 'training';
  const TRAINING_SESSION_URL = 'session';
  const TRAINING_SEARCH_SESSION_URL = 'search/session';
  const TRAINING_SEARCH_URL = 'search';
  const TRAINING_STATUS_ENCOURS = '200';

  //params context
  const { 
    setOpenSnackBar, 
    setSnackBarType, 
    setSnackBarMessage,

    triggerInfosPopin,

    loadThematicList,
    trainingThematicRows
  } = useContext(ContextBlock);

  const {
    training,
    trainingId,
    setTraining,
    setTrainingRows,
    updateTrainingStatus
  } = useContext(ContextTraining);

  //state
  const [trainingStaffingRows, setTrainingStaffingRows] = useState([]);
  const [trainingStaffingLoaded, setTrainingStaffingLoaded] = useState(false);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [trainingSearch, setTrainingSearch] = useState(null);


  useEffect(() => {
    if (trainingStaffingRows.length <= 0) {
      loadStaffingList();
    }
    if (trainingThematicRows.length <= 0) {
      loadThematicList();
    }
    if (trainingSearch == null) {
      loadSearchParams();
    }
  }, []);

  useEffect(() => {
    // trainingStaffingRows can legitimately be empty (nothing staffed yet), so we need an
    // explicit "loaded" flag to know it's safe to build the rows from it — otherwise this can
    // run before loadStaffingList() resolves and permanently miss the saved date sub-rows.
    if (trainingThematicRows.length > 0 && trainingStaffingLoaded && trainingSessions.length <= 0) {
      initTrainingSessionsList();
    }
  }, [training, trainingThematicRows, trainingStaffingLoaded]);

  const loadStaffingList = async () => {
    try {
      //config call API
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      }
      const response = await axios.get(`${TRAINING_URL}/${trainingId}/${TRAINING_SEARCH_SESSION_URL}`, config);
      //init rows
      setTrainingStaffingRows(response.data?.data);
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      }
      else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
      }
    } finally {
      setTrainingStaffingLoaded(true);
    }
  }

  const loadSearchParams = async () => {
    try {
      //config call API
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      }
      const response = await axios.get(`${TRAINING_URL}/${trainingId}/${TRAINING_SEARCH_URL}`, config);
      //init rows
      setTrainingSearch(response.data?.data);
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      }
      else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
      }
    }
  }

  const getTrainingStaffingsByIdTrainingSession = (id) => {
    return trainingStaffingRows.filter((staffing) => Number(staffing.id_training_session) === Number(id));
  }

  const getTrainingThematicById = (id) => {
    return trainingThematicRows.find((thematic) => String(thematic.id) === String(id));
  }

  const getTrainingThematicByName = (name) => {
    return trainingThematicRows.find((thematic) => thematic.name.toLowerCase().trim() === name.toLowerCase().trim());
  }

  const initTrainingSessionsList = () => {
    const searchRows = [];
    if(training.subsessions){
      // training.subsessions can now carry several rows per Digiforma module (one per saved
      // date) — the per-date fan-out below already comes from getTrainingStaffingsByIdTrainingSession,
      // so only one representative subsession per module should feed the outer loop.
      const uniqueSubsessions = Array.from(
        new Map(training.subsessions.map((s) => [s.id_training_session, s])).values()
      );
      for (const subsession of uniqueSubsessions) {
        const trainingStaffings = getTrainingStaffingsByIdTrainingSession(subsession.id_training_session);
        const program = subsession.id_digiforma_program
          ? getTrainingThematicById(subsession.id_digiforma_program)
          : getTrainingThematicByName(subsession.name);

        // One saved staffing row = one date sub-row. Fall back to the Digiforma subsession
        // itself when nothing has been staffed/saved yet for this training session.
        const staffingRows = trainingStaffings.length > 0 ? trainingStaffings : [null];

        for (const trainingStaffing of staffingRows) {
          searchRows.push({
            id: trainingStaffing ? trainingStaffing.id : subsession.id,
            name: subsession.name,
            subsessionId: subsession.id_training_session,
            digiformaStaffing: subsession.digiformaStaffing,

            nDays: trainingStaffing ? trainingStaffing.n_days : program ? program.durationInDays : 0,
            trainingThematicId: program ? program.id : null,
            tags: program ? program.tags : [],
            endDate: trainingStaffing ? moment(trainingStaffing.end_date) : subsession.end_date ? moment(subsession.end_date) : null,
            startDate: trainingStaffing ? moment(trainingStaffing.start_date) : subsession.start_date ? moment(subsession.start_date) : null
          });
        }
      }
    }

    const sortedRows = sortTrainingSessions(searchRows);
    setTrainingSessions(sortedRows);
  }

  const saveTrainingSessions = async () => {
    // Declared outside the try: if a row fails to save, the ids of the rows already created
    // must still be kept, otherwise the next save would create them again (duplicates)
    let updatedSessions = null;
    try {
      if (trainingSessions.length > 0) {
        updatedSessions = [...trainingSessions];
        const savedEntities = [];

        for (const [index, row] of trainingSessions.entries()) {
          const data = {
            id: row.id,
            id_training: trainingId,
            id_training_session: parseInt(row.subsessionId),
            id_digiforma_program: row.trainingThematicId,
            program_name: row.trainingThematicId ? (getTrainingThematicById(row.trainingThematicId)?.name ?? null) : null,
            n_days: !isNDaysInvalid(row.nDays) ? parseFloat(row.nDays) : 0,
            end_date: dayjs(row.endDate).format('YYYY-MM-DD'),
            start_date: dayjs(row.startDate).format('YYYY-MM-DD')
          };

          const config = {
            method: 'post',
            url: `${TRAINING_URL}/${trainingId}/${TRAINING_SEARCH_SESSION_URL}`,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
              'Content-Type': 'application/json'
            },
            data: data
          };
          const response = await axios(config);
          const savedEntity = response.data?.data;

          updatedSessions[index] = { ...updatedSessions[index], id: savedEntity?.id ?? updatedSessions[index].id };
          if (savedEntity) savedEntities.push(savedEntity);
        }

        // Rows aren't sorted while editing — saving is one of the moments they get re-ordered
        setTrainingSessions(sortTrainingSessions(updatedSessions));

        // Merge program_name from save responses into training state — no extra API call needed
        if (savedEntities.length > 0) {
          const savedMap = new Map(savedEntities.map(s => [Number(s.id_training_session), s]));
          const updatedTraining = {
            ...training,
            subsessions: (training.subsessions || []).map(sub => {
              const saved = savedMap.get(Number(sub.id_training_session));
              if (!saved) return sub;
              return { ...sub, program_name: saved.program_name, id_digiforma_program: saved.id_digiforma_program };
            })
          };
          setTraining(updatedTraining);
          setTrainingRows([updatedTraining]);
        }

        setSnackBarMessage('Les informations ont été enregistrées avec succès.');
        setSnackBarType('success');
        setOpenSnackBar(true);
        return {success : true};
      }
      return {success : false};
    } catch (err) {
      console.log(err);
      // Keep the ids of the rows saved before the error
      if (updatedSessions) setTrainingSessions(updatedSessions);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
        return {success : false};
      } else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
        return {success : false};
      }
    }
  }

   const removeTrainingSessions = async (id_training_session) => {
    try {
      if (id_training_session > 0) {
        const config = {
            method: 'delete',
            url: `${TRAINING_URL}/${trainingId}/${TRAINING_SESSION_URL}/${id_training_session}`,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
              'Content-Type': 'application/json'
            }
          };
          await axios(config);

        setSnackBarMessage('Suppression effectuée avec succès.');
        setSnackBarType('success');
        setOpenSnackBar(true);
        return {success : true};
      }
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      } else if (err.response?.status === 409) {
        setSnackBarMessage(err.response?.data?.message || 'Cette session ne peut pas être supprimée.');
        setSnackBarType('error');
        setOpenSnackBar(true);
        return {success : false};
      } else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
        return {success : false};
      }
    }
  }

  const saveTrainingSearch = async () => {
    try {
      if (trainingSearch !== null) {
        const data = {
          id_training: trainingId,
          id_region: trainingSearch.id_region == 0 ? null : parseInt(trainingSearch.id_region),
          id_instructor: trainingSearch.id_instructor == 0 ? null : parseInt(trainingSearch.id_instructor),
          instructor_already_known: trainingSearch.instructor_already_known,
          same_instructor: trainingSearch.same_instructor,
          search_full_france: trainingSearch.search_full_france,
          search_prometea: trainingSearch.search_prometea,
          search_subcontractor: trainingSearch.search_subcontractor,
          search_secafi: trainingSearch.search_secafi,
          search_atlantes: trainingSearch.search_atlantes,
          search_secafi_expertise: trainingSearch.search_secafi_expertise,
          internal_note: trainingSearch.internal_note
        };
        const config = {
          method: 'post',
          url: `${TRAINING_URL}/${trainingId}/${TRAINING_SEARCH_URL}`,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json'
          },
          data: data
        };
        await axios(config);

        setSnackBarMessage('Les informations ont été enregistrées avec succès.');
        setSnackBarType('success');
        setOpenSnackBar(true);
        return {success : true};
      }
      return {success : false};
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
        return {success : false};
      } else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
        return {success : false};
      }
    }
  }

  const saveTrainingSearchInternalNote = async () => {
    try {
      if (trainingSearch !== null) {
        const data = {
          id_training: trainingId,
          internal_note: trainingSearch.internal_note
        };
        const config = {
          method: 'post',
          url: `${TRAINING_URL}/${trainingId}/${TRAINING_SEARCH_URL}/internal_note`,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json'
          },
          data: data
        };
        await axios(config);

        setSnackBarMessage('La note interne été enregistrée avec succès.');
        setSnackBarType('success');
        setOpenSnackBar(true);
        return { success: true };
      }
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      } else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
        return { success: false };
      }
    }
  }

  const validTrainingSessions = async () => {
    const dateInError = checkTrainingSessionsDateInError();
    if (dateInError) return;
    const daysInError = checkTrainingSessionsDaysInError();
    if (daysInError) return;

    const saveSessions = await saveTrainingSessions();
    const saveSearch = await saveTrainingSearch();
    if(saveSessions.success === true && saveSearch.success === true){
      const updateTraining = await updateTrainingStatus(TRAINING_STATUS_ENCOURS);
      if (updateTraining.success === true) {
        navigate(`/formation/resultat?id=${trainingId}&key=${randomId()}`);
      }
    }
  }

  const checkTrainingSessionsDateInError = () => {
    // Every date option must have both dates (an empty one can't be saved)
    const dateMissing = trainingSessions.some(({ startDate, endDate }) => !startDate || !endDate || !dayjs(startDate).isValid() || !dayjs(endDate).isValid());
    if (dateMissing) {
      triggerInfosPopin(
        'Date manquante',
        'Chaque date doit avoir une date de début et une date de fin. Complétez ou supprimez les dates incomplètes avant d\'enregistrer.',
        'error'
      );
      return true;
    }

    const dateInError = trainingSessions.some(({ startDate, endDate }) => startDate && endDate && endDate.isBefore(startDate, 'day'));
    if (dateInError) {
      triggerInfosPopin(
        'Date erronée',
        'La date de fin ne peut pas être antérieure à la date de début.',
        'error'
      );
    }
    return dateInError;
  }

  // A missing/zero/negative/non-numeric n_days (e.g. a Digiforma program with no
  // configured duration) must never reach the API — it fails backend validation.
  const isNDaysInvalid = (nDays) => {
    if (nDays === null || nDays === undefined || nDays === '') return true;
    const parsed = parseFloat(nDays);
    return isNaN(parsed) || parsed <= 0;
  }

  const checkTrainingSessionsDaysInError = () => {
    const daysInError = trainingSessions.some((session) => isNDaysInvalid(session.nDays));
    if (daysInError) {
      triggerInfosPopin(
        'Nombre de jours invalide',
        'Le nombre de jours doit être renseigné, numérique et supérieur à 0 pour chaque session.',
        'error'
      );
    }
    return daysInError;
  }

  const sortTrainingSessions = (trainingSessionsList) => {
    // Normalisation : convert all in string "YYYY-MM-DD" or null
    const normalized = trainingSessionsList.map((s) => ({
      ...s,
      startDateCalendar: s.startDate ? s.startDate.format("YYYY-MM-DD") : null,
      endDateCalendar: s.endDate ? s.endDate.format("YYYY-MM-DD") : null,
    }));

    return normalized.sort((a, b) => {
      //subSessionId
      if (a.subsessionId !== b.subsessionId) {
        return a.subsessionId - b.subsessionId;
      }

      //thematicId
      if (a.trainingThematicId !== b.trainingThematicId) {
        return (a.trainingThematicId ?? '').localeCompare(b.trainingThematicId ?? '');
      }

      //startDate
      if (a.startDateCalendar === null && b.startDateCalendar !== null) return 1;
      if (a.startDateCalendar !== null && b.startDateCalendar === null) return -1;
      if (a.startDateCalendar !== null && b.startDateCalendar !== null) {
        if (a.startDateCalendar !== b.startDateCalendar) {
          return a.startDateCalendar.localeCompare(b.startDateCalendar);
        }
      }

      //endDate
      if (a.endDateCalendar === null && b.endDateCalendar !== null) return 1;
      if (a.endDateCalendar !== null && b.endDateCalendar === null) return -1;
      if (a.endDateCalendar !== null && b.endDateCalendar !== null) {
        return a.endDateCalendar.localeCompare(b.endDateCalendar);
      }

      return 0; // perfect equality
    });
  }


  return (
    <ContextTrainingSearch.Provider
      value=
      {{
        sortTrainingSessions,
        checkTrainingSessionsDateInError,
        isNDaysInvalid, checkTrainingSessionsDaysInError,
        trainingSessions, setTrainingSessions,
        trainingSearch, setTrainingSearch,
        saveTrainingSessions, validTrainingSessions,
        saveTrainingSearch, saveTrainingSearchInternalNote,
        removeTrainingSessions,
        getTrainingThematicById, getTrainingStaffingsByIdTrainingSession
      }}
    >
      {children}
    </ContextTrainingSearch.Provider>
  );
};

export { TrainingSearchProvider, ContextTrainingSearch };
